import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import Tippy from '@tippyjs/react';
import { FinancialSummaryMath } from '../../FinancialSummaryMath/FinancialSummaryMath';
import { splitShortfall } from '../utils/splitShortfall';
import { SplitPointCaption, SplitShortfallPill } from './SplitShortfallFlag';
import useIsMobile from '../../shared/hooks/useIsMobile';
import { isPoolPreviewActive, activeMember, activeMemberSettlementDue } from '../utils/buildPoolEngineInput';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

var formatterWithCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const containerStyle = {
  backgroundColor: 'var(--od-c-ffffff, #ffffff)',
  border: '1px solid var(--od-c-e9eaeb, #e9eaeb)',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  marginTop: '24px'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px 0 24px'
};

const titleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  lineHeight: '28px',
  color: 'var(--od-c-333333, #333333)'
};

const viewPLButtonStyle = {
  backgroundColor: 'var(--od-c-ffffff, #ffffff)',
  border: '1px solid var(--od-c-d5d7da, #d5d7da)',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  padding: '10px 14px',
  fontSize: '14px',
  fontWeight: '600',
  lineHeight: '20px',
  color: 'var(--od-c-414651, #414651)',
  cursor: 'pointer',
  textDecoration: 'none'
};

const dividerStyle = {
  height: '1px',
  backgroundColor: 'var(--od-c-e9eaeb, #e9eaeb)',
  margin: '20px 0 0 0'
};

const contentStyle = {
  padding: '32px'
};

const cardBaseStyle = {
  borderRadius: '16px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  height: '100%'
};

const offerCardStyle = {
  ...cardBaseStyle,
  backgroundColor: 'var(--od-c-fafafa, #fafafa)',
  border: '1px solid var(--od-c-e9eaeb, #e9eaeb)'
};

const forecastCardStyle = {
  ...cardBaseStyle,
  backgroundColor: 'var(--od-c-f6fafe, #f6fafe)',
  border: '1px solid var(--od-c-e4f2fc, #e4f2fc)'
};

const actualsCardStyle = {
  ...cardBaseStyle,
  backgroundColor: 'var(--od-c-eff4ff, #eff4ff)',
  border: '1px solid var(--od-c-d1e0ff, #d1e0ff)'
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%'
};

const cardTitleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  lineHeight: '24px',
  color: 'var(--od-c-333333, #333333)'
};

const profitableBadgeStyle = {
  border: '1.5px solid #8ac926',
  borderRadius: '9999px',
  padding: '4px 12px',
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '20px',
  color: '#6d9f1e'
};

const notProfitableBadgeStyle = {
  border: '1.5px solid #f04438',
  borderRadius: '9999px',
  padding: '4px 12px',
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '20px',
  color: '#d92d20'
};

const mainMetricsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%'
};

const metricGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end'
};

const metricLabelStyle = {
  fontSize: '14px',
  fontWeight: '600',
  lineHeight: '20px',
  color: 'var(--od-c-535862, #535862)'
};

const metricValueStyle = {
  fontSize: '36px',
  fontWeight: '600',
  lineHeight: '44px',
  color: 'var(--od-c-333333, #333333)',
  letterSpacing: '-0.72px'
};

const metricValueZeroStyle = {
  ...metricValueStyle,
  color: 'var(--od-c-717680, #717680)'
};

const detailedMetricsStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  marginTop: '8px'
};

const detailRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
};

const detailLabelStyle = {
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '24px',
  color: 'var(--od-c-535862, #535862)',
  padding: '0 8px 0 0'
};

const detailLabelBoldStyle = {
  ...detailLabelStyle,
  fontWeight: '600'
};

const detailValueStyle = {
  fontSize: '16px',
  fontWeight: '600',
  lineHeight: '24px',
  color: 'var(--od-c-333333, #333333)',
  textAlign: 'right',
  padding: '0 0 0 8px',
  whiteSpace: 'nowrap'
};

const detailValueZeroStyle = {
  ...detailValueStyle,
  color: 'var(--od-c-717680, #717680)'
};

const formatValue = (value, isExpense = false) => {
  if (value === undefined || value === null) return '$0';
  const num = Number(value);
  if (num === 0) return isExpense ? '($0)' : '$0';
  const fmt = (num % 1 === 0) ? formatter : formatterWithCents;
  if (isExpense) {
    return `(${fmt.format(Math.abs(num))})`;
  }
  return fmt.format(num);
};

const isZeroOrEmpty = (value) => {
  if (value === undefined || value === null) return true;
  return Number(value) === 0;
};

const formatConverted = (value, globalCurrency) => {
  if (!globalCurrency || value === undefined || value === null) return '';
  return globalCurrency.formatted(value);
};

const convertedValueStyle = {
  fontSize: '14px',
  fontWeight: '500',
  color: 'var(--od-c-717680, #717680)',
  textAlign: 'right',
  padding: '0 8px',
  whiteSpace: 'nowrap'
};

const convertedMetricValueStyle = {
  fontSize: '18px',
  fontWeight: '500',
  color: 'var(--od-c-717680, #717680)'
};

const SummaryCard = ({
  title,
  style,
  data,
  budget,
  offer,
  showCurrencyConversion,
  globalCurrency,
  variant = 'default',
  shortfall = null
}) => {
  const isMobile = useIsMobile();
  const isProfitable = data && data.venueNetPotential > 0;
  const isNotProfitable = data && data.venueNetPotential < 0;
  const isActuals = variant === 'actuals';
  const venueNetLabel = isActuals ? 'Venue Net' : 'Venue Net Potential';
  const artistEarningsLabel = isActuals ? 'Artist Earnings' : 'Potential Artist Earnings';
  const preSplitDeductionsIsDeduction = data?.preSplitDeductions >= 0;

  const mobileCardStyle = useMemo(
    () => (isMobile ? { ...style, padding: '16px' } : style),
    [isMobile, style]
  );
  const mobileMetricValueStyle = useMemo(
    () => (isMobile
      ? { ...metricValueStyle, fontSize: '24px', lineHeight: '32px', letterSpacing: '-0.48px' }
      : metricValueStyle),
    [isMobile]
  );
  const mobileMetricValueZeroStyle = useMemo(
    () => (isMobile
      ? { ...metricValueZeroStyle, fontSize: '24px', lineHeight: '32px', letterSpacing: '-0.48px' }
      : metricValueZeroStyle),
    [isMobile]
  );
  const mobileConvertedMetricValueStyle = useMemo(
    () => (isMobile
      ? { ...convertedMetricValueStyle, fontSize: '14px' }
      : convertedMetricValueStyle),
    [isMobile]
  );
  const mobileDetailValueStyle = useMemo(
    () => (isMobile
      ? { ...detailValueStyle, whiteSpace: 'normal', wordBreak: 'break-word' }
      : detailValueStyle),
    [isMobile]
  );
  const mobileDetailValueZeroStyle = useMemo(
    () => (isMobile
      ? { ...detailValueZeroStyle, whiteSpace: 'normal', wordBreak: 'break-word' }
      : detailValueZeroStyle),
    [isMobile]
  );
  const mobileConvertedValueStyle = useMemo(
    () => (isMobile
      ? { ...convertedValueStyle, whiteSpace: 'normal', wordBreak: 'break-word' }
      : convertedValueStyle),
    [isMobile]
  );
  const mobileCardHeaderStyle = useMemo(
    () => (isMobile
      ? { ...cardHeaderStyle, flexWrap: 'wrap', gap: '4px' }
      : cardHeaderStyle),
    [isMobile]
  );

  return (
    <div style={mobileCardStyle}>
      <div style={mobileCardHeaderStyle}>
        <span style={cardTitleStyle}>{title}</span>
        {isProfitable && (
          <span style={profitableBadgeStyle}>Profitable</span>
        )}
        {isNotProfitable && (
          <span style={notProfitableBadgeStyle}>Not Profitable</span>
        )}
      </div>
      <div style={mainMetricsStyle}>
        <div style={metricGroupStyle}>
          <span style={metricLabelStyle}>{artistEarningsLabel}</span>
          <span style={isZeroOrEmpty(data?.potentialArtistEarnings)
            ? mobileMetricValueZeroStyle : mobileMetricValueStyle}>
            {formatValue(data?.potentialArtistEarnings)}
          </span>
          {showCurrencyConversion && globalCurrency && (
            <span style={mobileConvertedMetricValueStyle}>
              {formatConverted(data?.potentialArtistEarnings, globalCurrency)}
            </span>
          )}
        </div>
        <div style={metricGroupStyle}>
          <span style={metricLabelStyle}>
            {venueNetLabel}
            <Tippy
              content="The venue's net across all artists and expenses on this event."
              placement="top"
              animation="shift-away"
              theme="material"
            >
              <a href="#" className="text-muted ml-2" onClick={(e) => e.preventDefault()}>
                <i className="fas fa-info-circle"></i>
              </a>
            </Tippy>
          </span>
          <span style={isZeroOrEmpty(data?.venueNetPotential)
            ? mobileMetricValueZeroStyle : mobileMetricValueStyle}>
            {formatValue(data?.venueNetPotential)}
          </span>
          {showCurrencyConversion && globalCurrency && (
            <span style={mobileConvertedMetricValueStyle}>
              {formatConverted(data?.venueNetPotential, globalCurrency)}
            </span>
          )}
        </div>
      </div>
      <div style={detailedMetricsStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={detailLabelStyle}>Gross Potential</td>
              {showCurrencyConversion && globalCurrency && (
                <td style={mobileConvertedValueStyle}>
                  {formatConverted(data?.grossPotential, globalCurrency)}
                </td>
              )}
              <td style={isZeroOrEmpty(data?.grossPotential)
                ? mobileDetailValueZeroStyle : mobileDetailValueStyle}>
                {formatValue(data?.grossPotential)}
              </td>
            </tr>

            {data?.beforeAdjustedDeductions > 0 && (
              <>
                <tr>
                  <td style={detailLabelStyle}>Before Adjusted Deductions</td>
                  {showCurrencyConversion && globalCurrency && (
                    <td style={mobileConvertedValueStyle}>
                      ({formatConverted(data?.beforeAdjustedDeductions, globalCurrency)})
                    </td>
                  )}
                  <td style={mobileDetailValueStyle}>
                    {formatValue(data?.beforeAdjustedDeductions, true)}
                  </td>
                </tr>
                <tr>
                  <td style={detailLabelStyle}>Adjusted Gross Potential</td>
                  {showCurrencyConversion && globalCurrency && (
                    <td style={mobileConvertedValueStyle}>
                      {formatConverted(data?.adjustedGrossPotential, globalCurrency)}
                    </td>
                  )}
                  <td style={mobileDetailValueStyle}>
                    {formatValue(data?.adjustedGrossPotential)}
                  </td>
                </tr>
              </>
            )}

            {data?.admissionTax > 0 && (
              <tr>
                <td style={detailLabelStyle}>Admission Tax (added on top)</td>
                {showCurrencyConversion && globalCurrency && (
                  <td style={mobileConvertedValueStyle}>
                    {formatConverted(data?.admissionTax, globalCurrency)}
                  </td>
                )}
                <td style={mobileDetailValueStyle}>
                  {formatValue(data?.admissionTax)}
                </td>
              </tr>
            )}

            <tr>
              <td style={detailLabelStyle}>Expenses</td>
              {showCurrencyConversion && globalCurrency && (
                <td style={mobileConvertedValueStyle}>
                  ({formatConverted(data?.expenses, globalCurrency)})
                </td>
              )}
              <td style={isZeroOrEmpty(data?.expenses)
                ? mobileDetailValueZeroStyle : mobileDetailValueStyle}>
                {formatValue(data?.expenses, true)}
              </td>
            </tr>

            <tr>
              <td style={detailLabelBoldStyle}>Show Net, After Expenses</td>
              {showCurrencyConversion && globalCurrency && (
                <td style={mobileConvertedValueStyle}>
                  {formatConverted(data?.showNetAfterExpenses, globalCurrency)}
                </td>
              )}
              <td style={isZeroOrEmpty(data?.showNetAfterExpenses)
                ? mobileDetailValueZeroStyle : mobileDetailValueStyle}>
                {formatValue(data?.showNetAfterExpenses)}
              </td>
            </tr>

            {data?.finalExpenses > 0 && (
              <>
                <tr>
                  <td style={detailLabelStyle}>
                    Final Expenses
                    <Tippy
                      content={
                        <div style={{ fontSize: '10px' }}>
                          <p style={{ marginBottom: 0 }}>
                            Final Expenses include any expense that require the
                            total expenses or show net to be calculated beforehand:
                          </p>
                          <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                            <li>Percentage of Expenses calculations</li>
                            <li>Percentage of Net calculations</li>
                          </ul>
                        </div>
                      }
                      placement="top"
                      maxWidth={390}
                      animation="shift-away"
                      theme="material"
                    >
                      <a href="#" className="text-muted ml-2"
                        onClick={(e) => e.preventDefault()}>
                        <i className="fas fa-info-circle"></i>
                      </a>
                    </Tippy>
                  </td>
                  {showCurrencyConversion && globalCurrency && (
                    <td style={mobileConvertedValueStyle}>
                      ({formatConverted(data?.finalExpenses, globalCurrency)})
                    </td>
                  )}
                  <td style={mobileDetailValueStyle}>
                    {formatValue(data?.finalExpenses, true)}
                  </td>
                </tr>
                <tr>
                  <td style={detailLabelBoldStyle}>Show Net, After Final Expenses</td>
                  {showCurrencyConversion && globalCurrency && (
                    <td style={mobileConvertedValueStyle}>
                      {formatConverted(data?.showNetAfterFinalExpenses, globalCurrency)}
                    </td>
                  )}
                  <td style={mobileDetailValueStyle}>
                    {formatValue(data?.showNetAfterFinalExpenses)}
                  </td>
                </tr>
              </>
            )}

            {data?.promoterProfit > 0 && (
              <>
                <tr>
                  <td style={detailLabelStyle}>Promoter Profit</td>
                  {showCurrencyConversion && globalCurrency && (
                    <td style={mobileConvertedValueStyle}>
                      ({formatConverted(data?.promoterProfit, globalCurrency)})
                    </td>
                  )}
                  <td style={mobileDetailValueStyle}>
                    {formatValue(data?.promoterProfit, true)}
                  </td>
                </tr>
                <tr>
                  <td style={detailLabelBoldStyle}>Show Net, After Promoter Profit</td>
                  {showCurrencyConversion && globalCurrency && (
                    <td style={mobileConvertedValueStyle}>
                      {formatConverted(data?.showNetAfterPromoterProfit, globalCurrency)}
                    </td>
                  )}
                  <td style={mobileDetailValueStyle}>
                    {formatValue(data?.showNetAfterPromoterProfit)}
                  </td>
                </tr>
              </>
            )}

            <tr>
              <td style={detailLabelStyle}>Split Point</td>
              {showCurrencyConversion && globalCurrency && (
                <td style={mobileConvertedValueStyle}>
                  {formatConverted(data?.splitPoint, globalCurrency)}
                </td>
              )}
              <td style={{ ...(isZeroOrEmpty(data?.splitPoint)
                ? mobileDetailValueZeroStyle : mobileDetailValueStyle), verticalAlign: 'top' }}>
                {formatValue(data?.splitPoint)}
                {shortfall?.unreachable && <SplitPointCaption />}
              </td>
            </tr>

            {data?.preSplitDeductions != null && (
              <tr>
                <td style={detailLabelStyle}>
                  Total Pre-Split Deductions
                  <Tippy
                    content="Everything taken off gross before the artist's percentage is applied. The percentage is calculated on what's left. What counts depends on the deal's Basis, under Calculation method. Total Deductions come off after the split."
                    placement="top"
                    animation="shift-away"
                    theme="material"
                  >
                    <a
                      href="#"
                      className="text-muted ml-2"
                      aria-label="What are Total Pre-Split Deductions?"
                      onClick={(e) => e.preventDefault()}
                    >
                      <i className="fas fa-info-circle" aria-hidden="true"></i>
                    </a>
                  </Tippy>
                </td>
                {showCurrencyConversion && globalCurrency && (
                  <td style={mobileConvertedValueStyle}>
                    {preSplitDeductionsIsDeduction
                      ? `(${formatConverted(data.preSplitDeductions, globalCurrency)})`
                      : formatConverted(data.preSplitDeductions, globalCurrency)}
                  </td>
                )}
                <td style={{ ...(isZeroOrEmpty(data.preSplitDeductions)
                  ? mobileDetailValueZeroStyle : mobileDetailValueStyle), verticalAlign: 'top' }}>
                  {formatValue(data.preSplitDeductions, preSplitDeductionsIsDeduction)}
                </td>
              </tr>
            )}

            <tr>
              <td style={detailLabelStyle}>Artist Amount from Split</td>
              {showCurrencyConversion && globalCurrency && (
                <td style={mobileConvertedValueStyle}>
                  {formatConverted(data?.artistAmountFromSplit, globalCurrency)}
                </td>
              )}
              <td style={{ ...(isZeroOrEmpty(data?.artistAmountFromSplit)
                ? mobileDetailValueZeroStyle : mobileDetailValueStyle), verticalAlign: 'top' }}>
                {formatValue(data?.artistAmountFromSplit)}
                {shortfall && <SplitShortfallPill shortfall={shortfall} />}
              </td>
            </tr>

            {data?.totalAdditions > 0 && (
              <tr>
                <td style={detailLabelStyle}>Total Additions</td>
                {showCurrencyConversion && globalCurrency && (
                  <td style={mobileConvertedValueStyle}>
                    {formatConverted(data?.totalAdditions, globalCurrency)}
                  </td>
                )}
                <td style={mobileDetailValueStyle}>
                  {formatValue(data?.totalAdditions)}
                </td>
              </tr>
            )}

            {data?.totalDeductions > 0 && (
              <tr>
                <td style={detailLabelStyle}>Total Deductions</td>
                {showCurrencyConversion && globalCurrency && (
                  <td style={mobileConvertedValueStyle}>
                    ({formatConverted(data?.totalDeductions, globalCurrency)})
                  </td>
                )}
                <td style={mobileDetailValueStyle}>
                  {formatValue(data?.totalDeductions, true)}
                </td>
              </tr>
            )}

            {offer?.has_tax_withholding && (
              <tr>
                <td style={detailLabelStyle}>Tax Withholding</td>
                {showCurrencyConversion && globalCurrency && (
                  <td style={mobileConvertedValueStyle}>
                    ({formatConverted(data?.taxWithholding, globalCurrency)})
                  </td>
                )}
                <td style={isZeroOrEmpty(data?.taxWithholding)
                  ? mobileDetailValueZeroStyle : mobileDetailValueStyle}>
                  {formatValue(data?.taxWithholding, true)}
                </td>
              </tr>
            )}
            {data?.ancillaryIncome > 0 && (
              <tr>
                <td style={detailLabelStyle}>Ancillary Income</td>
                {showCurrencyConversion && globalCurrency && (
                  <td style={mobileConvertedValueStyle}>
                    {formatConverted(data?.ancillaryIncome, globalCurrency)}
                  </td>
                )}
                <td style={mobileDetailValueStyle}>
                  {formatValue(data?.ancillaryIncome)}
                </td>
              </tr>
            )}

            {data?.ancillaryExpenses > 0 && (
              <tr>
                <td style={detailLabelStyle}>Ancillary Expenses</td>
                {showCurrencyConversion && globalCurrency && (
                  <td style={mobileConvertedValueStyle}>
                    ({formatConverted(data?.ancillaryExpenses, globalCurrency)})
                  </td>
                )}
                <td style={mobileDetailValueStyle}>
                  {formatValue(data?.ancillaryExpenses, true)}
                </td>
              </tr>
            )}

          </tbody>
        </table>
      </div>
    </div>
  );
};

const V3FinancialSummarySection = ({
  calculated,
  team,
  offer,
  budget,
  eventVenueNet,
  showCurrencyConversion,
  globalCurrency,
  eventOffers = [],
  headerActions,
  embedded
}) => {
  const isMobile = useIsMobile();
  const financialSummaryCalculated = useMemo(() => {
    if (!budget || Object.keys(budget).length === 0) {
      return null;
    }

    try {
      const calculator = new FinancialSummaryMath(offer, budget, false);
      return JSON.parse(calculator.calculate());
    } catch (e) {
      console.error('FinancialSummaryMath calculation error:', e);
      return null;
    }
  }, [offer, budget]);

  // O-2783: in Pool mode the locally-recomputed `financialSummaryCalculated`
  // is WRONG — it runs the raw offer (whose own guarantee is 0; the deal lives
  // on the pool) against budgetForMath (which double-counts sibling guarantees).
  // Prefer the page's `calculated`, which already ran the pool engine offer
  // against the right budget. In Independent mode, behavior is unchanged.
  const poolActive = isPoolPreviewActive(offer, offer?.lineup);
  const effectiveCalculated = poolActive
    ? (calculated || {})
    : (financialSummaryCalculated || calculated);
  const hasCalculated = effectiveCalculated && Object.keys(effectiveCalculated).length > 0;

  // In Pool mode the artist's "Potential Artist Earnings" is their individual
  // (post-tax) pool take, not artistEarnings.artistSettlementDue (which the
  // engine overwrites with the SUM across all members).
  const artistEarningsFor = (singleOfferValue, poolMembers) => (
    poolActive ? activeMemberSettlementDue(offer, poolMembers) : singleOfferValue
  );

  // The "Tax Withholding" row must also be per-member in Pool mode — otherwise a
  // post-tax per-artist headline sits above the withholding for the WHOLE pool.
  // Per-member tax = the member's pre-tax take minus their post-tax settlement.
  const taxWithholdingFor = (singleOfferValue, poolMembers) => {
    if (!poolActive) return singleOfferValue;
    const m = activeMember(offer, poolMembers);
    if (!m) return 0;
    const take = Number(m.take) || 0;
    const net = m.settlementTake != null ? Number(m.settlementTake) : take;
    return Math.max(0, take - net);
  };

  // Sibling artist guarantees are now baked into the budget passed in via
  // V3FinancePage's `budgetForMath` (see buildSiblingGuaranteeExpenses,
  // O-2993), so expensesSection totals already include them. The earlier
  // display-only `totalArtistGuarantees` reduce-sum has been removed to
  // avoid double-counting against the math layer.

  // Venue Net Potential is an EVENT-level figure (the venue's bottom line
  // across all artists on the event), so it overrides the per-offer
  // venueNetPotential when supplied. Potential Artist Earnings stays
  // per-tab. Falls back to the per-offer value for single-artist events,
  // where the two are equal anyway (O-2993 follow-up).
  const venueNetFor = (mode, perOfferValue) => (
    eventVenueNet && eventVenueNet[mode] != null ? eventVenueNet[mode] : perOfferValue
  );

  const shortfall = hasCalculated && !poolActive ? splitShortfall(effectiveCalculated, offer, budget) : null;

  const offerData = hasCalculated ? {
    potentialArtistEarnings: artistEarningsFor(
      effectiveCalculated.artistEarnings?.artistSettlementDue,
      effectiveCalculated.poolMembers,
    ),
    venueNetPotential: venueNetFor('offer', effectiveCalculated.showSummary?.venueNetPotential),
    grossPotential: (effectiveCalculated.ticketsSection?.externalTotal || 0) +
      (effectiveCalculated.revenueSection?.externalTotal || 0),
    ancillaryIncome: effectiveCalculated.showSummary?.ancillaryIncome || 0,
    ancillaryExpenses: effectiveCalculated.showSummary?.ancillaryExpenses || 0,
    beforeAdjustedDeductions: effectiveCalculated.beforeAdjustedDeductionsSection?.externalTotal,
    adjustedGrossPotential: effectiveCalculated.showSummary?.netBoxOfficeReceipts,
    admissionTax: effectiveCalculated.showSummary?.admissionTax,
    expenses: effectiveCalculated.expensesSection?.externalTotal || 0,
    showNetAfterExpenses: effectiveCalculated.showSummary?.showNetAfterExpenses,
    finalExpenses: effectiveCalculated.finalExpensesSection?.externalTotal,
    showNetAfterFinalExpenses: effectiveCalculated.showSummary?.showNetAfterFinalExpenses,
    promoterProfit: effectiveCalculated.showSummary?.promoterProfit,
    showNetAfterPromoterProfit: effectiveCalculated.showSummary?.showNetAfterPromoterProfit,
    splitPoint: effectiveCalculated.showSummary?.splitPoint,
    preSplitDeductions: effectiveCalculated.showSummary?.preSplitDeductions,
    artistAmountFromSplit: effectiveCalculated.showSummary?.artistPercentage,
    totalAdditions: effectiveCalculated.artistEarnings?.totalAdditions,
    totalDeductions: effectiveCalculated.artistEarnings?.totalDeductions,
    taxWithholding: taxWithholdingFor(
      effectiveCalculated.artistEarnings?.taxWithholding,
      effectiveCalculated.poolMembers,
    )
  } : {};

  const forecastData = hasCalculated ? {
    potentialArtistEarnings: artistEarningsFor(
      effectiveCalculated.artistEarningsForecast?.artistSettlementDue,
      effectiveCalculated.poolMembersForecast,
    ),
    venueNetPotential: venueNetFor('forecast', effectiveCalculated.showSummary?.venueNetPotentialForecast),
    grossPotential: (effectiveCalculated.ticketsSection?.externalForecastTotal || 0) +
      (effectiveCalculated.revenueSection?.externalForecastTotal || 0),
    ancillaryIncome: effectiveCalculated.showSummary?.ancillaryIncomeForecast || 0,
    ancillaryExpenses: effectiveCalculated.showSummary?.ancillaryExpensesForecast || 0,
    beforeAdjustedDeductions: effectiveCalculated.beforeAdjustedDeductionsSection?.externalForecastTotal,
    adjustedGrossPotential: effectiveCalculated.showSummary?.netBoxOfficeReceiptsForecast,
    admissionTax: effectiveCalculated.showSummary?.admissionTaxForecast,
    expenses: effectiveCalculated.expensesSection?.externalForecastTotal || 0,
    showNetAfterExpenses: effectiveCalculated.showSummary?.showNetAfterExpensesForecast,
    finalExpenses: effectiveCalculated.finalExpensesSection?.externalForecastTotal,
    showNetAfterFinalExpenses: effectiveCalculated.showSummary?.showNetAfterFinalExpensesForecast,
    promoterProfit: effectiveCalculated.showSummary?.promoterProfitForecast,
    showNetAfterPromoterProfit: effectiveCalculated.showSummary?.showNetAfterPromoterProfitForecast,
    splitPoint: effectiveCalculated.showSummary?.splitPointForecast,
    preSplitDeductions: effectiveCalculated.showSummary?.preSplitDeductionsForecast,
    artistAmountFromSplit: effectiveCalculated.showSummary?.artistPercentageForecast,
    totalAdditions: effectiveCalculated.artistEarningsForecast?.totalAdditions,
    totalDeductions: effectiveCalculated.artistEarningsForecast?.totalDeductions,
    taxWithholding: taxWithholdingFor(
      effectiveCalculated.artistEarningsForecast?.taxWithholding,
      effectiveCalculated.poolMembersForecast,
    )
  } : {};

  const actualsData = hasCalculated ? {
    potentialArtistEarnings: artistEarningsFor(
      effectiveCalculated.artistEarningsActual?.artistSettlementDue,
      effectiveCalculated.poolMembersActual,
    ),
    venueNetPotential: venueNetFor('actual', effectiveCalculated.showSummary?.venueNetPotentialActual),
    grossPotential: (effectiveCalculated.ticketsSection?.externalActualTotal || 0) +
      (effectiveCalculated.revenueSection?.externalActualTotal || 0),
    ancillaryIncome: effectiveCalculated.showSummary?.ancillaryIncomeActual || 0,
    ancillaryExpenses: effectiveCalculated.showSummary?.ancillaryExpensesActual || 0,
    beforeAdjustedDeductions: effectiveCalculated.beforeAdjustedDeductionsSection?.externalActualTotal,
    adjustedGrossPotential: effectiveCalculated.showSummary?.netBoxOfficeReceiptsActual,
    admissionTax: effectiveCalculated.showSummary?.admissionTaxActual,
    expenses: effectiveCalculated.expensesSection?.externalActualTotal || 0,
    showNetAfterExpenses: effectiveCalculated.showSummary?.showNetAfterExpensesActual,
    finalExpenses: effectiveCalculated.finalExpensesSection?.externalActualTotal,
    showNetAfterFinalExpenses: effectiveCalculated.showSummary?.showNetAfterFinalExpensesActual,
    promoterProfit: effectiveCalculated.showSummary?.promoterProfitActual,
    showNetAfterPromoterProfit: effectiveCalculated.showSummary?.showNetAfterPromoterProfitActual,
    splitPoint: effectiveCalculated.showSummary?.splitPointActual,
    preSplitDeductions: effectiveCalculated.showSummary?.preSplitDeductionsActual,
    artistAmountFromSplit: effectiveCalculated.showSummary?.artistPercentageActual,
    totalAdditions: effectiveCalculated.artistEarningsActual?.totalAdditions,
    totalDeductions: effectiveCalculated.artistEarningsActual?.totalDeductions,
    taxWithholding: taxWithholdingFor(
      effectiveCalculated.artistEarningsActual?.taxWithholding,
      effectiveCalculated.poolMembersActual,
    )
  } : {};

  const cards = (
    <>
      <div className="col-12 col-lg-6 col-xl-4 mb-2">
        <SummaryCard
          title="Offer"
          style={offerCardStyle}
          data={offerData}
          budget={budget}
          offer={offer}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          shortfall={shortfall}
        />
      </div>
      <div className="col-12 col-lg-6 col-xl-4 mb-2">
        <SummaryCard
          title="Forecast"
          style={forecastCardStyle}
          data={forecastData}
          budget={budget}
          offer={offer}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
        />
      </div>
      <div className="col-12 col-lg-6 col-xl-4 mb-2">
        <SummaryCard
          title="Actuals"
          style={actualsCardStyle}
          data={actualsData}
          budget={budget}
          offer={offer}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          variant="actuals"
        />
      </div>
    </>
  );

  if (embedded) {
    return cards;
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Financial Summary</span>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {offer.calendar_event?.id && (
            <a
              href={`/teams/${team.id}/confirms/${offer.calendar_event.id}/profit_and_loss`}
              style={viewPLButtonStyle}
            >
              View P&L
            </a>
          )}
          {headerActions}
        </div>
      </div>
      <div style={dividerStyle} />
      <div style={isMobile ? { ...contentStyle, padding: '12px' } : contentStyle}>
        <div className="form-row">
          {cards}
        </div>
      </div>
    </div>
  );
};

V3FinancialSummarySection.propTypes = {
  calculated: PropTypes.object,
  team: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  budget: PropTypes.object,
  eventVenueNet: PropTypes.object,
  showCurrencyConversion: PropTypes.bool,
  globalCurrency: PropTypes.object,
  eventOffers: PropTypes.array,
  headerActions: PropTypes.node,
  embedded: PropTypes.bool,
};

export default V3FinancialSummarySection;
